<template>
  <div class="route-making-container" dir="rtl">
    <!-- ==================== PAGE HEADER ==================== -->
    <div class="rm-header-row mb-6">
      <div class="rm-header-titles">
        <div class="d-flex align-center mb-1">
          <router-link to="/routes" class="rm-badge-section text-decoration-none">
            <v-icon size="14" color="#082C69" class="me-1">mdi-arrow-right</v-icon>
            المسارات
          </router-link>
        </div>
        <h1 class="rm-main-title">{{ mode === 1 ? 'تعديل المسار' : 'إضافة مسار جديد' }}</h1>
        <p class="rm-subtitle">قم بإضافة مسار جديد وتحديد محطاته وتسعيره</p>
      </div>

      <div class="rm-header-actions">
        <v-btn
          depressed
          class="rm-btn-secondary me-2"
          @click="confirmCancel"
        >
          <v-icon right size="18" class="ms-1">mdi-close</v-icon>
          إلغاء
        </v-btn>
        <v-btn
          v-if="currentStep === 2"
          depressed
          class="rm-btn-primary"
          :loading="submitting"
          @click="saveRoute"
        >
          <v-icon right size="18" class="ms-1">mdi-content-save-check-outline</v-icon>
          {{ mode === 1 ? 'حفظ التعديلات' : 'حفظ وإنشاء المسار' }}
        </v-btn>
      </div>
    </div>

    <!-- ==================== 2-STEP STEPPER ==================== -->
    <div class="rm-stepper-card mb-6">
      <div class="rm-stepper">
        <!-- Step 1: معلومات المسار -->
        <div
          class="rm-step-item"
          :class="{
            'step-active': currentStep === 1,
            'step-completed': currentStep > 1,
          }"
          @click="goToStep(1)"
        >
          <div class="rm-step-circle">
            <v-icon v-if="currentStep > 1" size="18" color="#FFFFFF">mdi-check-bold</v-icon>
            <span v-else>1</span>
          </div>
          <div class="rm-step-info">
            <span class="rm-step-seq">الخطوة الأولى</span>
            <span class="rm-step-title">معلومات المسار</span>
          </div>
        </div>

        <!-- Connecting Line -->
        <div class="rm-step-divider" :class="{ 'divider-completed': currentStep > 1 }"></div>

        <!-- Step 2: إضافة المحطات -->
        <div
          class="rm-step-item"
          :class="{
            'step-active': currentStep === 2,
            'step-disabled': currentStep < 2 && !isStep1Valid,
          }"
          @click="isStep1Valid && goToStep(2)"
        >
          <div class="rm-step-circle">
            <span>2</span>
          </div>
          <div class="rm-step-info">
            <span class="rm-step-seq">الخطوة الثانية</span>
            <span class="rm-step-title">إضافة المحطات</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== STEP 1: معلومات المسار ==================== -->
    <div v-show="currentStep === 1" class="rm-step1-container">
      <div class="rm-card rm-step1-card">
        <div class="rm-card-header mb-5">
          <div class="rm-icon-header-box me-3">
            <v-icon size="24" color="#082C69">mdi-form-textbox</v-icon>
          </div>
          <div>
            <h2 class="rm-card-title">بيانات المسار الأساسية</h2>
            <p class="rm-card-subtitle">أدخل اسم المسار بشكل دقيق وواضح لتمييزه في شبكة النقل</p>
          </div>
        </div>

        <!-- رقم المسار (Edit Mode: Read-Only) -->
        <div v-if="mode === 1 && route_code" class="rm-form-group mb-5">
          <label class="rm-form-label">
            رقم المسار
            <span class="rm-readonly-badge ms-2">ثابت وغير قابل للتعديل</span>
          </label>
          <div>
            <div class="rm-code-display-box">
              <v-icon size="18" color="#082C69" class="me-2">mdi-pound</v-icon>
              <span class="rm-code-display-value">{{ route_code }}</span>
            </div>
          </div>
          <span class="rm-field-hint mt-1">
            رقم المسار هو المعرف التشغيلي الدائم لهذا المسار ولا يتغير عند تعديل البيانات.
          </span>
        </div>

        <div class="rm-form-group mb-6">
          <label class="rm-form-label">
            اسم المسار
            <span class="rm-required-star">*</span>
          </label>
          <v-text-field
            v-model="route_name"
            dense
            outlined
            hide-details="auto"
            placeholder="مثال: مسار التجمع الخامس - المعادي"
            class="rm-custom-input"
            :error-messages="step1Error"
            @input="step1Error = ''"
            @keyup.enter="handleContinueToStep2"
          >
            <template v-slot:prepend-inner>
              <v-icon size="20" color="#64748B" class="me-2">mdi-road-variant</v-icon>
            </template>
          </v-text-field>
          <span class="rm-field-hint mt-2">
            يُفضل أن يعبر الاسم عن نقطتي البداية والنهاية للمسار (مثال: محطة رمسيس - مدينة نصر).
          </span>
        </div>

        <div class="d-flex justify-end gap-3 pt-4 border-top">
          <v-btn
            depressed
            class="rm-btn-secondary me-3"
            to="/routes"
          >
            إلغاء
          </v-btn>
          <v-btn
            depressed
            class="rm-btn-primary"
            @click="handleContinueToStep2"
          >
            التالي: إضافة المحطات
            <v-icon left size="18" class="me-1">mdi-arrow-left</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- ==================== STEP 2: إضافة المحطات + التسعير ==================== -->
    <div v-show="currentStep === 2" class="rm-step2-container">
      <div class="rm-step2-grid">

        <!-- ==================== ROW 1: [إضافة المحطات] | [معاينة المسار على الخريطة] ==================== -->
        <div class="rm-row-paired">
          <!-- Left Controls: إضافة المحطات (40-45%) -->
          <div class="rm-col-controls">
            <div class="rm-card rm-control-card rm-full-height">
              <div class="rm-card-header mb-4">
                <div class="d-flex align-center">
                  <div class="rm-icon-header-box me-3">
                    <v-icon size="22" color="#082C69">mdi-map-marker-plus</v-icon>
                  </div>
                  <div>
                    <h2 class="rm-card-title">إضافة المحطات</h2>
                    <p class="rm-card-subtitle">ابحث عن محطة محفوظة أو أضف موقعاً جديداً</p>
                  </div>
                </div>
              </div>

              <!-- Option A: Choose from saved stops -->
              <div class="rm-control-section mb-4">
                <label class="rm-form-label mb-2">
                  اختيار من المحطات المحفوظة
                </label>
                <div class="mb-2">
                  <v-autocomplete
                    v-model="model"
                    :items="items"
                    :loading="isSearching"
                    :search-input.sync="search"
                    hide-selected
                    item-text="Description"
                    outlined
                    dense
                    hide-details
                    placeholder="ابحث بالاسم أو اختر محطة..."
                    class="rm-custom-select"
                    return-object
                    append-icon="mdi-chevron-down"
                    @change="stopSelected"
                  ></v-autocomplete>
                </div>
                <v-btn
                  depressed
                  block
                  class="rm-btn-secondary"
                  :disabled="!selectedSavedStop"
                  @click="addSavedStop"
                >
                  <v-icon right size="18" class="ms-1">mdi-plus</v-icon>
                  إضافة المحطة المحفوظة
                </v-btn>
              </div>

              <!-- Divider with OR -->
              <div class="rm-or-divider my-3">
                <span>أو</span>
              </div>

              <!-- Option B: Universal Station-Location Input -->
              <div class="rm-control-section mb-3">
                <label class="rm-form-label mb-1">
                  إضافة موقع جديد (بحث شامل / إحداثيات / رابط خرائط)
                </label>
                <div class="text-caption text--secondary mb-2" style="font-size: 11.5px; line-height: 1.4;">
                  يدعم: اسم المكان، الإحداثيات (<code>30.0444, 31.2357</code>)، روابط Google Maps الكاملة أو المختصرة (<code>maps.app.goo.gl</code>)، أو Plus Codes.
                </div>

                <div class="rm-gmap-input-wrapper d-flex align-center mb-2" style="gap: 6px;">
                  <GmapAutocomplete
                    id="stop-address"
                    ref="gmapAutocomplete"
                    class="rm-gmap-input flex-grow-1"
                    :options="{ fields: ['geometry', 'formatted_address', 'name', 'place_id'] }"
                    @place_changed="setPlace"
                    @input.native="onUniversalInputChange"
                    @paste.native="onUniversalInputPaste"
                    @keydown.native.enter.prevent="resolveUniversalLocationInput"
                    placeholder="اكتب اسم الموقع أو الصق إحداثيات / رابط خرائط جوجل..."
                  />
                  <v-btn
                    depressed
                    small
                    color="#082C69"
                    class="white--text px-3"
                    style="height: 42px; border-radius: 8px; font-weight: 700;"
                    :loading="resolvingLocation"
                    @click="resolveUniversalLocationInput"
                    title="تحليل الرابط أو الإحداثيات أو النص"
                  >
                    <v-icon left size="16">mdi-crosshairs-gps</v-icon>
                    تحديد
                  </v-btn>
                </div>

                <div v-if="locationResolveError" class="rm-resolve-error mb-2 pa-2 rounded" style="background: #FEF2F2; border: 1px solid #FECACA; color: #DC2626; font-size: 12px; font-weight: 600;">
                  <v-icon size="15" color="#DC2626" class="me-1">mdi-alert-circle-outline</v-icon>
                  {{ locationResolveError }}
                </div>

                <!-- Resolved Station Preview Card -->
                <div
                  v-if="resolvedStationPreview"
                  class="rm-resolved-preview mb-2 pa-3 rounded-lg"
                  style="background: #F0FDF4; border: 1px solid #BBF7D0;"
                >
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div class="d-flex align-center">
                      <v-icon size="18" color="#16A34A" class="me-1">mdi-check-decagram</v-icon>
                      <span class="font-weight-bold" style="color: #166534; font-size: 12.5px;">
                        معاينة المحطة المكتشفة
                      </span>
                    </div>
                    <div class="d-flex align-center" style="gap: 4px;">
                      <v-chip x-small label color="#DCFCE7" text-color="#15803D" class="font-weight-bold">
                        {{ getSourceTypeLabel(resolvedStationPreview.source_type) }}
                      </v-chip>
                      <v-btn icon x-small @click="clearResolvedStationPreview" title="مسح المعاينة">
                        <v-icon size="15" color="#64748B">mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <div class="mb-2">
                    <label class="d-block font-weight-bold mb-1" style="font-size: 11.5px; color: #1E293B;">
                      اسم المحطة في المسار:
                    </label>
                    <v-text-field
                      v-model="resolvedStationPreview.name"
                      dense
                      outlined
                      hide-details
                      background-color="white"
                      placeholder="اسم المحطة"
                      class="rm-custom-input"
                    ></v-text-field>
                  </div>

                  <div class="text-caption mb-1" style="color: #334155; line-height: 1.4;">
                    <strong>العنوان:</strong> {{ resolvedStationPreview.address }}
                  </div>
                  <div class="text-caption" style="color: #475569; direction: ltr; text-align: right; font-family: monospace;">
                    {{ Number(resolvedStationPreview.lat).toFixed(6) }}, {{ Number(resolvedStationPreview.lng).toFixed(6) }}
                  </div>
                </div>

                <v-btn
                  depressed
                  block
                  class="rm-btn-secondary"
                  :loading="resolvingLocation"
                  :disabled="!resolvedStationPreview && !universalLocationRaw"
                  @click="handleAddLocationBtnClick"
                >
                  <v-icon right size="18" class="ms-1">mdi-plus</v-icon>
                  {{ resolvedStationPreview ? 'إضافة المحطة المكتشفة للمسار' : 'تحليل وإضافة الموقع' }}
                </v-btn>
              </div>

              <v-spacer></v-spacer>

              <!-- Helper Info Banner -->
              <div class="rm-info-banner mt-2">
                <v-icon size="18" color="#082C69" class="me-2 flex-shrink-0">mdi-information-outline</v-icon>
                <span class="rm-info-text">
                  يمكنك أيضاً النقر مباشرة على أي نقطة على الخريطة لإضافتها كمحطة وتسميتها فوراً.
                </span>
              </div>
            </div>
          </div>

          <!-- Right Main: معاينة المسار على الخريطة (55-60%) -->
          <div class="rm-col-main">
            <div class="rm-card rm-map-card rm-full-height">
              <div class="rm-card-header d-flex align-center justify-space-between mb-3">
                <div class="d-flex align-center">
                  <div class="rm-icon-header-box me-3">
                    <v-icon size="22" color="#082C69">mdi-map</v-icon>
                  </div>
                  <div>
                    <h2 class="rm-card-title">معاينة المسار على الخريطة</h2>
                    <p class="rm-card-subtitle">
                      {{ stops.length > 0 ? `تم تحديد ${stops.length} محطات على خط سير المسار` : 'انقر على الخريطة أو اختر من المحطات لإضافتها' }}
                    </p>
                  </div>
                </div>
                <div class="d-flex align-center">
                  <v-chip
                    small
                    label
                    class="font-weight-bold rm-stops-counter-chip me-2"
                  >
                    <v-icon left size="15" color="#082C69">mdi-bus-stop</v-icon>
                    {{ stops.length }} محطة
                  </v-chip>
                  <v-btn
                    v-if="markers.length > 1"
                    text
                    small
                    class="rm-fit-btn"
                    @click="fitMapBounds"
                  >
                    <v-icon size="16" class="me-1">mdi-fit-to-screen-outline</v-icon>
                    إظهار الكل
                  </v-btn>
                </div>
              </div>

              <!-- Map Viewport -->
              <div class="rm-map-viewport flex-grow-1">
                <GoogleMapLoader
                  ref="mapLoader"
                  :enabled="true"
                  :center="center"
                  :selected="selectedItem"
                  :zoom="zoom"
                  :apiKey="apiKey"
                  :markers="markers"
                  @map-click="handleMapClick"
                  :polylines="polyline"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- ==================== ROW 2: [التسعير] | [قائمة المحطات + ملخص المسار] ==================== -->
        <div class="rm-row-paired">
          <!-- Left Controls: التسعير (40-45%) -->
          <div class="rm-col-controls">
            <div class="rm-card rm-pricing-card rm-full-height">
              <div class="rm-card-header mb-3">
                <div class="d-flex align-center">
                  <div class="rm-icon-header-box me-3">
                    <v-icon size="22" color="#082C69">mdi-currency-usd</v-icon>
                  </div>
                  <div>
                    <h2 class="rm-card-title">تسعير المسار</h2>
                    <p class="rm-card-subtitle">تحديد أسعار التذاكر وباقات الاشتراكات للمسار</p>
                  </div>
                </div>
              </div>

              <!-- SECTION A: تسعير الرحلة -->
              <div class="d-flex align-center mb-2">
                <v-icon size="16" color="#082C69" class="me-1">mdi-ticket-outline</v-icon>
                <span class="font-weight-bold text-subtitle-2" style="color: #082C69;">تسعير الرحلة</span>
              </div>

              <!-- Compact Trip Pricing Inputs Grid -->
              <div class="rm-pricing-inputs-grid mb-3">
                <!-- Full Trip Price -->
                <div class="rm-form-group">
                  <label class="rm-form-label">
                    سعر الرحلة بالكامل (جنيه)
                  </label>
                  <v-text-field
                    v-model="full_trip_price"
                    dense
                    outlined
                    hide-details
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder="سعر المسار كاملاً..."
                    class="rm-custom-input"
                  >
                    <template v-slot:append>
                      <span class="text-caption font-weight-bold text--secondary">ج.م</span>
                    </template>
                  </v-text-field>
                </div>

                <!-- Minimum Percentage -->
                <div class="rm-form-group">
                  <label class="rm-form-label">
                    نسبة الحد الأدنى للأجرة (%)
                  </label>
                  <v-text-field
                    v-model="minimum_percentage"
                    dense
                    outlined
                    hide-details
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    placeholder="نسبة الحد الأدنى..."
                    class="rm-custom-input"
                  >
                    <template v-slot:append>
                      <span class="text-caption font-weight-bold text--secondary">%</span>
                    </template>
                  </v-text-field>
                </div>
              </div>

              <!-- Derived Summary Row -->
              <div v-if="computedMinimumPrice" class="rm-derived-summary-box mb-4">
                <div class="rm-derived-item">
                  <span class="rm-derived-label">قيمة الحد الأدنى:</span>
                  <span class="rm-derived-val">{{ computedMinimumPrice }} ج.م</span>
                </div>
                <div v-if="computedPricePerKm" class="rm-derived-item">
                  <span class="rm-derived-label">سعر الكيلو:</span>
                  <span class="rm-derived-val rm-val-highlight">{{ computedPricePerKm }} ج.م / كم</span>
                </div>
                <div v-if="computedTotalDistance" class="rm-derived-item">
                  <span class="rm-derived-label">إجمالي مسافة المسار:</span>
                  <span class="rm-derived-val">{{ computedTotalDistance }} كم</span>
                </div>
              </div>

              <v-divider class="my-3"></v-divider>

              <!-- SECTION B: باقات الاشتراك -->
              <div class="d-flex align-center mb-1">
                <v-icon size="16" color="#082C69" class="me-1">mdi-wallet-membership</v-icon>
                <span class="font-weight-bold text-subtitle-2" style="color: #082C69;">باقات الاشتراك</span>
              </div>
              <p class="text-caption text--secondary mb-3">
                تحديد نسب الخصم للباقات الأسبوعية والشهرية على هذا المسار.
              </p>

              <!-- Side-by-Side Packages Grid -->
              <div class="rm-packages-row mb-3">
                <!-- Package 1: الباقة الأسبوعية -->
                <div class="rm-pkg-card">
                  <div class="rm-pkg-header mb-2">
                    <div class="d-flex align-center">
                      <v-icon size="18" color="#082C69" class="me-1">mdi-calendar-week</v-icon>
                      <span class="font-weight-bold text-subtitle-2" style="color: #082C69;">الباقة الأسبوعية</span>
                    </div>
                    <div class="d-flex align-center">
                      <span class="rm-tag-days me-1">7 أيام</span>
                      <span class="rm-tag-first">10 رحلات</span>
                    </div>
                  </div>

                  <div class="rm-form-group mb-2">
                    <label class="rm-form-label text-caption mb-1">
                      نسبة الخصم الأسبوعية (%)
                    </label>
                    <v-text-field
                      v-model="weekly_discount_percentage"
                      dense
                      outlined
                      hide-details
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      placeholder="مثال: 10"
                      class="rm-custom-input"
                    >
                      <template v-slot:append>
                        <span class="text-caption font-weight-bold text--secondary">%</span>
                      </template>
                    </v-text-field>
                  </div>

                  <!-- Weekly Full-Route Preview -->
                  <div v-if="weeklyFullBasePrice" class="rm-pkg-preview">
                    <div class="rm-pkg-preview-title">
                      السعر التقديري للباقة للمسار بالكامل:
                    </div>
                    <div class="rm-pkg-preview-row">
                      <span>قبل الخصم (10 × {{ full_trip_price }} ج.م):</span>
                      <span class="font-weight-medium">{{ weeklyFullBasePrice }} ج.م</span>
                    </div>
                    <div class="rm-pkg-preview-row text-danger" v-if="weekly_discount_percentage">
                      <span>قيمة الخصم ({{ weekly_discount_percentage }}%):</span>
                      <span class="font-weight-medium">-{{ weeklyDiscountAmount }} ج.م</span>
                    </div>
                    <div class="rm-pkg-preview-total">
                      <span>السعر النهائي:</span>
                      <span class="rm-pkg-total-val">{{ weeklyFinalPrice }} ج.م</span>
                    </div>
                  </div>
                </div>

                <!-- Package 2: الباقة الشهرية -->
                <div class="rm-pkg-card">
                  <div class="rm-pkg-header mb-2">
                    <div class="d-flex align-center">
                      <v-icon size="18" color="#082C69" class="me-1">mdi-calendar-month</v-icon>
                      <span class="font-weight-bold text-subtitle-2" style="color: #082C69;">الباقة الشهرية</span>
                    </div>
                    <div class="d-flex align-center">
                      <span class="rm-tag-days me-1">30 يوم</span>
                      <span class="rm-tag-first">44 رحلة</span>
                    </div>
                  </div>

                  <div class="rm-form-group mb-2">
                    <label class="rm-form-label text-caption mb-1">
                      نسبة الخصم الشهرية (%)
                    </label>
                    <v-text-field
                      v-model="monthly_discount_percentage"
                      dense
                      outlined
                      hide-details
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      placeholder="مثال: 15"
                      class="rm-custom-input"
                    >
                      <template v-slot:append>
                        <span class="text-caption font-weight-bold text--secondary">%</span>
                      </template>
                    </v-text-field>
                  </div>

                  <!-- Monthly Full-Route Preview -->
                  <div v-if="monthlyFullBasePrice" class="rm-pkg-preview">
                    <div class="rm-pkg-preview-title">
                      السعر التقديري للباقة للمسار بالكامل:
                    </div>
                    <div class="rm-pkg-preview-row">
                      <span>قبل الخصم (44 × {{ full_trip_price }} ج.م):</span>
                      <span class="font-weight-medium">{{ monthlyFullBasePrice }} ج.م</span>
                    </div>
                    <div class="rm-pkg-preview-row text-danger" v-if="monthly_discount_percentage">
                      <span>قيمة الخصم ({{ monthly_discount_percentage }}%):</span>
                      <span class="font-weight-medium">-{{ monthlyDiscountAmount }} ج.م</span>
                    </div>
                    <div class="rm-pkg-preview-total">
                      <span>السعر النهائي:</span>
                      <span class="rm-pkg-total-val">{{ monthlyFinalPrice }} ج.م</span>
                    </div>
                  </div>
                </div>
              </div>

              <span class="rm-field-hint">
                ملاحظة: السعر التقديري أعلاه هو للمسار بالكامل. يتم احتساب سعر اشتراك العميل الفعلي وفق محطتي ركوبه ونزوله تلقائياً.
              </span>
            </div>
          </div>

          <!-- Right Main: قائمة المحطات + ملخص المسار (55-60%) -->
          <div class="rm-col-main">
            <div class="rm-card rm-stops-and-summary-card rm-full-height">
              <div class="rm-card-header d-flex align-center justify-space-between mb-3">
                <div class="d-flex align-center">
                  <div class="rm-icon-header-box me-3">
                    <v-icon size="22" color="#082C69">mdi-format-list-numbered-rtl</v-icon>
                  </div>
                  <div>
                    <h2 class="rm-card-title">قائمة المحطات المحددة</h2>
                    <p class="rm-card-subtitle">ترتيب وتفاصيل المحطات التي سيمر بها المسار</p>
                  </div>
                </div>
                <span class="rm-table-count">{{ stops.length }} محطة مسجلة</span>
              </div>

              <!-- Empty State -->
              <div v-if="stops.length === 0" class="rm-stops-empty py-8 text-center flex-grow-1 d-flex flex-column align-center justify-center">
                <div class="rm-empty-icon-wrap mb-2">
                  <v-icon size="44" color="#94A3B8">mdi-map-marker-path</v-icon>
                </div>
                <h4 class="rm-empty-sub mb-1">لم يتم إضافة محطات بعد</h4>
                <p class="rm-empty-text text--secondary mb-0">
                  استخدم نموذج التحكم في الصف العلوي أو انقر مباشرة على الخريطة لإضافة المحطة الأولى.
                </p>
              </div>

              <!-- Stops Table -->
              <div v-else class="rm-stops-table-wrap flex-grow-1">
                <table class="rm-modern-table">
                  <thead>
                    <tr>
                      <th class="rm-th-index text-center">#</th>
                      <th class="rm-th-name">اسم المحطة</th>
                      <th class="rm-th-addr">العنوان / الموقع</th>
                      <th class="rm-th-dir">المسار من المحطة السابقة</th>
                      <th class="rm-th-actions text-center">الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(element, index) in stops"
                      :key="element.place_id || element.id || index"
                      :class="['rm-table-row', { 'row-active': selectedIdx === index }]"
                      @click="selectStop(element, index)"
                    >
                      <!-- 1. Order Index -->
                      <td class="rm-td-index text-center">
                        <span
                          class="rm-order-badge"
                          :class="{
                            'badge-first': index === 0,
                            'badge-last': index === stops.length - 1 && stops.length > 1,
                          }"
                        >
                          {{ index + 1 }}
                        </span>
                      </td>

                      <!-- 2. Stop Name -->
                      <td class="rm-td-name">
                        <div class="d-flex align-center">
                          <div class="rm-route-icon-circle me-2">
                            <v-icon
                              size="14"
                              :color="index === 0 ? '#10B981' : (index === stops.length - 1 && stops.length > 1 ? '#EF4444' : '#082C69')"
                            >
                              {{ index === 0 ? 'mdi-record-circle-outline' : (index === stops.length - 1 && stops.length > 1 ? 'mdi-map-marker-check' : 'mdi-bus-stop') }}
                            </v-icon>
                          </div>
                          <div>
                            <span class="rm-stop-name-text">{{ element.name }}</span>
                            <span v-if="index === 0" class="rm-tag-first ms-2">نقطة البداية</span>
                            <span v-else-if="index === stops.length - 1 && stops.length > 1" class="rm-tag-last ms-2">نقطة النهاية</span>
                          </div>
                        </div>
                      </td>

                      <!-- 3. Address -->
                      <td class="rm-td-addr">
                        <span class="rm-address-text" :title="element.address">
                          {{ element.address || '—' }}
                        </span>
                      </td>

                      <!-- 4. Direction Selector -->
                      <td class="rm-td-dir">
                        <div v-if="index !== 0">
                          <v-select
                            v-model="chosen_routes[index - 1]"
                            :items="ordered_directions[index - 1] || []"
                            :item-text="(item) => item.summary"
                            :item-value="(item) => item.index"
                            dense
                            outlined
                            hide-details
                            class="rm-direction-select"
                            placeholder="اختر المسار"
                            append-icon="mdi-chevron-down"
                            @change="setup_polyline"
                          ></v-select>
                        </div>
                        <span v-else class="rm-dir-start-tag">نقطة انطلاق المسار</span>
                      </td>

                      <!-- 5. Actions -->
                      <td class="rm-td-actions text-center">
                        <div class="rm-actions-group">
                          <button
                            type="button"
                            class="rm-action-btn"
                            :disabled="index === 0"
                            title="تحريك لأعلى"
                            @click.stop="moveStopUp(index)"
                          >
                            <v-icon size="16">mdi-arrow-up</v-icon>
                          </button>
                          <button
                            type="button"
                            class="rm-action-btn"
                            :disabled="index === stops.length - 1"
                            title="تحريك لأسفل"
                            @click.stop="moveStopDown(index)"
                          >
                            <v-icon size="16">mdi-arrow-down</v-icon>
                          </button>
                          <button
                            type="button"
                            class="rm-action-btn btn-delete"
                            title="حذف المحطة"
                            @click.stop="deleteMarker(index)"
                          >
                            <v-icon size="16">mdi-trash-can-outline</v-icon>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Route Summary Metrics at bottom of card -->
              <div class="rm-summary-metrics-section mt-3 pt-3 border-top">
                <div class="d-flex align-center mb-2">
                  <v-icon size="18" color="#082C69" class="me-1">mdi-chart-timeline-variant</v-icon>
                  <span class="font-weight-bold text-caption" style="color: #082C69;">ملخص المسار</span>
                </div>
                <div class="rm-summary-metrics-grid">
                  <!-- Metric 1: عدد المحطات -->
                  <div class="rm-metric-box">
                    <span class="rm-metric-label">عدد المحطات</span>
                    <div class="rm-metric-val-wrap">
                      <span class="rm-metric-val">{{ stops.length }}</span>
                    </div>
                    <span class="rm-metric-caption">محطات مسجلة</span>
                  </div>

                  <!-- Metric 2: المسافة الكلية -->
                  <div class="rm-metric-box">
                    <span class="rm-metric-label">المسافة الكلية</span>
                    <div class="rm-metric-val-wrap">
                      <span v-if="computedTotalDistance" class="rm-metric-val">{{ computedTotalDistance }} <small>كم</small></span>
                      <span v-else class="rm-metric-empty"></span>
                    </div>
                    <span class="rm-metric-caption">إجمالي خط السير</span>
                  </div>

                  <!-- Metric 3: الوقت التقريبي -->
                  <div class="rm-metric-box">
                    <span class="rm-metric-label">الوقت التقريبي</span>
                    <div class="rm-metric-val-wrap">
                      <span v-if="computedTotalDuration" class="rm-metric-val">{{ computedTotalDuration }}</span>
                      <span v-else class="rm-metric-empty"></span>
                    </div>
                    <span class="rm-metric-caption">تقدير القيادة</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ==================== HORIZONTAL BOTTOM ACTIONS BAR ==================== -->
        <div class="rm-card rm-bottom-actions-card d-flex align-center">
          <v-btn
            depressed
            class="rm-btn-primary me-3"
            :loading="submitting"
            @click="saveRoute"
          >
            <v-icon right size="18" class="ms-1">mdi-content-save-check-outline</v-icon>
            {{ mode === 1 ? 'حفظ التعديلات' : 'حفظ وإنشاء المسار' }}
          </v-btn>

          <v-btn
            depressed
            class="rm-btn-secondary me-3"
            @click="goToStep(1)"
          >
            <v-icon right size="18" class="ms-1">mdi-arrow-right</v-icon>
            السابق: تعديل اسم المسار
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn
            text
            color="#64748B"
            class="rm-btn-text"
            @click="confirmCancel"
          >
            إلغاء والعودة للمسارات
          </v-btn>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import GoogleMapLoader from "@/components/GoogleMapLoader.vue";
import { Keys } from "/src/config.js";

export default {
  name: "RouteMakingCreateRoute",
  components: {
    GoogleMapLoader,
  },
  data() {
    return {
      apiKey: Keys.GOOGLE_MAPS_API_KEY,
      currentStep: 1,
      route_name: "",
      route_id: null,
      route_code: "",
      full_trip_price: "",
      minimum_percentage: "",
      weekly_discount_percentage: "",
      monthly_discount_percentage: "",
      step1Error: "",
      mode: 0, // 0: create, 1: edit
      submitting: false,
      isSearching: false,
      model: null,
      search: null,
      selectedSavedStop: null,
      items: [],
      currentPlace: null,
      universalLocationRaw: "",
      resolvingLocation: false,
      locationResolveError: "",
      resolvedStationPreview: null,
      stops: [],
      markers: [],
      ordered_directions: [],
      chosen_routes: [],
      polyline: [],
      center: {
        lat: Keys.VUE_APP_ORIGIN_LAT,
        lng: Keys.VUE_APP_ORIGIN_LNG,
      },
      zoom: 12,
      selectedItem: null,
      selectedIdx: null,
      colors: [
        "#082C69",
        "#10B981",
        "#F59E0B",
        "#3B82F6",
        "#8B5CF6",
        "#EC4899",
        "#06B6D4",
        "#EF4444",
      ],
    };
  },
  computed: {
    isStep1Valid() {
      return Boolean(this.route_name && this.route_name.trim().length > 0);
    },
    computedMinimumPrice() {
      const f = parseFloat(this.full_trip_price);
      const p = parseFloat(this.minimum_percentage);
      if (!isNaN(f) && f > 0 && !isNaN(p) && p > 0) {
        return ((f * p) / 100).toFixed(2);
      }
      return null;
    },
    computedPricePerKm() {
      const f = parseFloat(this.full_trip_price);
      const minPrice = parseFloat(this.computedMinimumPrice);
      const d = parseFloat(this.computedTotalDistance);
      if (!isNaN(f) && f > 0 && !isNaN(minPrice) && !isNaN(d) && d > 0) {
        const rem = f - minPrice;
        return (rem / d).toFixed(2);
      }
      return null;
    },
    weeklyFullBasePrice() {
      const f = parseFloat(this.full_trip_price);
      if (!isNaN(f) && f > 0) {
        return (f * 10).toFixed(2);
      }
      return null;
    },
    weeklyDiscountAmount() {
      const base = parseFloat(this.weeklyFullBasePrice);
      const d = parseFloat(this.weekly_discount_percentage);
      if (!isNaN(base) && base > 0 && !isNaN(d) && d >= 0) {
        return ((base * d) / 100).toFixed(2);
      }
      return "0.00";
    },
    weeklyFinalPrice() {
      const base = parseFloat(this.weeklyFullBasePrice);
      const disc = parseFloat(this.weeklyDiscountAmount);
      if (!isNaN(base) && base > 0) {
        return Math.max(0, base - (isNaN(disc) ? 0 : disc)).toFixed(2);
      }
      return null;
    },
    monthlyFullBasePrice() {
      const f = parseFloat(this.full_trip_price);
      if (!isNaN(f) && f > 0) {
        return (f * 44).toFixed(2);
      }
      return null;
    },
    monthlyDiscountAmount() {
      const base = parseFloat(this.monthlyFullBasePrice);
      const d = parseFloat(this.monthly_discount_percentage);
      if (!isNaN(base) && base > 0 && !isNaN(d) && d >= 0) {
        return ((base * d) / 100).toFixed(2);
      }
      return "0.00";
    },
    monthlyFinalPrice() {
      const base = parseFloat(this.monthlyFullBasePrice);
      const disc = parseFloat(this.monthlyDiscountAmount);
      if (!isNaN(base) && base > 0) {
        return Math.max(0, base - (isNaN(disc) ? 0 : disc)).toFixed(2);
      }
      return null;
    },
    computedTotalDistance() {
      if (!this.ordered_directions || this.ordered_directions.length === 0) return null;
      let totalKm = 0;
      for (let i = 0; i < this.ordered_directions.length; i++) {
        const chosenIdx = this.chosen_routes[i] || 0;
        const directions = this.ordered_directions[i];
        if (directions && directions[chosenIdx] && directions[chosenIdx].overview_path) {
          const path = directions[chosenIdx].overview_path;
          for (let j = 1; j < path.length; j++) {
            totalKm += this.calcDistance(
              path[j - 1].lat,
              path[j - 1].lng,
              path[j].lat,
              path[j].lng
            );
          }
        }
      }
      return totalKm > 0 ? totalKm.toFixed(2) : null;
    },
    computedTotalDuration() {
      if (!this.ordered_directions || this.ordered_directions.length === 0) return null;
      let totalSeconds = 0;
      let hasDuration = false;
      for (let i = 0; i < this.ordered_directions.length; i++) {
        const chosenIdx = this.chosen_routes[i] || 0;
        const directions = this.ordered_directions[i];
        if (directions && directions[chosenIdx] && directions[chosenIdx].duration) {
          const sec = parseInt(directions[chosenIdx].duration, 10);
          if (!isNaN(sec) && sec > 0) {
            totalSeconds += sec;
            hasDuration = true;
          }
        }
      }
      if (!hasDuration || totalSeconds <= 0) return null;
      const mins = Math.round(totalSeconds / 60);
      if (mins >= 60) {
        const hours = Math.floor(mins / 60);
        const remMins = mins % 60;
        return `${hours} ساعة ${remMins > 0 ? `و ${remMins} د` : ''}`;
      }
      return `${mins} دقيقة`;
    },
  },
  watch: {
    search() {
      if (this.items.length > 0) return;
      this.loadAllStops();
    },
  },
  mounted() {
    this.center.lat = parseFloat(this.center.lat);
    this.center.lng = parseFloat(this.center.lng);
    this.loadAllStops();

    // Check if route_name or route_id was passed in route params
    if (this.$route.params.route_name) {
      this.mode = 0;
      this.route_name = this.$route.params.route_name;
      this.geolocate();
      // If user came with pre-filled name, allow moving to Step 2
      this.currentStep = 2;
    } else if (this.$route.params.route_id) {
      this.mode = 1;
      this.route_id = this.$route.params.route_id;
      this.route_name = this.$route.params.new_route_name || "";
      this.currentStep = 2;
      this.fetchStops();
    } else {
      this.mode = 0;
      this.geolocate();
      this.currentStep = 1;
    }
  },
  methods: {
    goToStep(step) {
      if (step === 2) {
        if (!this.isStep1Valid) {
          this.step1Error = "يرجى إدخال اسم المسار للمتابعة";
          return;
        }
        this.step1Error = "";
        this.currentStep = 2;
        this.$nextTick(() => {
          this.fitMapBounds();
        });
      } else {
        this.currentStep = 1;
      }
    },
    handleContinueToStep2() {
      this.goToStep(2);
    },
    geolocate() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          this.center = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
        });
      }
    },
    loadAllStops() {
      this.isSearching = true;
      axios
        .get(`/stops/all`)
        .then((response) => {
          const stops = Array.isArray(response.data) ? response.data : [];
          this.items = stops.map((element) => {
            const Description = `${element.name} - ${element.address}`;
            return {
              id: element.id,
              name: element.name,
              address: element.address,
              lat: element.lat,
              lng: element.lng,
              place_id: element.place_id,
              Description,
            };
          });
        })
        .catch((error) => {
          console.error("Failed to load stops:", error);
        })
        .finally(() => {
          this.isSearching = false;
        });
    },
    findDuplicateInRoute(lat, lng, placeId = null, targetId = null) {
      const targetLat = parseFloat(lat);
      const targetLng = parseFloat(lng);
      const hasCoords = !isNaN(targetLat) && !isNaN(targetLng);

      for (let i = 0; i < this.stops.length; i++) {
        const existing = this.stops[i];
        // 1. Same real DB ID
        if (targetId && existing.id && Number(targetId) > 0 && Number(existing.id) > 0 && Number(existing.id) === Number(targetId)) {
          return { duplicate: true, existingStop: existing, reason: "same_id" };
        }
        // 2. Same Google place_id
        if (
          placeId &&
          existing.place_id &&
          String(existing.place_id) === String(placeId) &&
          !String(placeId).startsWith("coord_")
        ) {
          return { duplicate: true, existingStop: existing, reason: "same_place_id" };
        }
        // 3. Proximity threshold (<= 30 meters)
        if (hasCoords) {
          const exLat = parseFloat(existing.lat);
          const exLng = parseFloat(existing.lng);
          if (!isNaN(exLat) && !isNaN(exLng)) {
            const distKm = this.calcDistance(targetLat, targetLng, exLat, exLng);
            if (distKm <= 0.03) {
              return { duplicate: true, existingStop: existing, reason: "proximity" };
            }
          }
        }
      }
      return null;
    },
    findMatchingGlobalStop(lat, lng, placeId = null) {
      if (!Array.isArray(this.items) || this.items.length === 0) return null;
      const targetLat = parseFloat(lat);
      const targetLng = parseFloat(lng);
      const hasCoords = !isNaN(targetLat) && !isNaN(targetLng);

      // 1. Same Google place_id
      if (placeId && !String(placeId).startsWith("coord_")) {
        const byPlace = this.items.find(
          (s) => s.place_id && String(s.place_id) === String(placeId) && s.id && Number(s.id) > 0
        );
        if (byPlace) return byPlace;
      }

      // 2. Proximity threshold (<= 30 meters) among saved stops
      if (hasCoords) {
        let closest = null;
        let minKm = 0.03;
        for (let i = 0; i < this.items.length; i++) {
          const s = this.items[i];
          const sLat = parseFloat(s.lat);
          const sLng = parseFloat(s.lng);
          if (!isNaN(sLat) && !isNaN(sLng)) {
            const distKm = this.calcDistance(targetLat, targetLng, sLat, sLng);
            if (distKm <= minKm) {
              minKm = distKm;
              closest = s;
            }
          }
        }
        if (closest && closest.id && Number(closest.id) > 0) {
          return closest;
        }
      }

      return null;
    },
    isDuplicateOrTooCloseStop(lat, lng, placeId = null) {
      return this.findDuplicateInRoute(lat, lng, placeId, null) !== null;
    },
    showDuplicateStopWarning(existingStop = null, candidateName = "") {
      const existingName = (existingStop && existingStop.name ? existingStop.name : "").trim();
      const newName = (candidateName || "").trim();
      const isSameName = (existingName && newName && existingName.toLowerCase() === newName.toLowerCase());
      const text = isSameName
        ? "هذه المحطة مضافة بالفعل إلى المسار."
        : "يوجد بالفعل محطة في نفس الموقع ضمن هذا المسار.";

      this.$notify({
        title: "محطة مكررة",
        text,
        type: "warn",
      });
    },
    stopSelected(s) {
      if (!s) return;
      s.lat = parseFloat(s.lat);
      s.lng = parseFloat(s.lng);
      this.selectedSavedStop = s;
    },
    addSavedStop() {
      if (!this.selectedSavedStop) return;
      const stop = { ...this.selectedSavedStop };
      stop.lat = parseFloat(stop.lat);
      stop.lng = parseFloat(stop.lng);

      const dup = this.findDuplicateInRoute(stop.lat, stop.lng, stop.place_id, stop.id);
      if (dup) {
        this.showDuplicateStopWarning(dup.existingStop, stop.name);
        return;
      }
      this.stops.push(stop);
      this.addMarker(stop);
      this.selectedSavedStop = null;
      this.model = null;

      if (this.stops.length > 1) {
        const lastStop = this.stops[this.stops.length - 1];
        const beforeLastStop = this.stops[this.stops.length - 2];
        this.getAddedStopRoute(lastStop, beforeLastStop);
      }
    },
    getSourceTypeLabel(sourceType) {
      const map = {
        google_places_autocomplete: "Google Places",
        direct_coordinates: "إحداثيات مباشرة",
        short_google_maps_url: "رابط خرائط مختصر",
        google_maps_url: "رابط Google Maps",
        place_id: "Place ID",
        plus_code: "Plus Code",
        text_search: "بحث نصي",
      };
      return map[sourceType] || "موقع مكتشف";
    },
    onUniversalInputChange(e) {
      const val = e && e.target ? e.target.value : "";
      this.universalLocationRaw = val ? val.trim() : "";
      this.locationResolveError = "";
    },
    onUniversalInputPaste(e) {
      this.$nextTick(() => {
        const el = this.$refs.gmapAutocomplete && this.$refs.gmapAutocomplete.$el ? this.$refs.gmapAutocomplete.$el : (e && e.target ? e.target : null);
        const val = el && el.value ? el.value.trim() : "";
        this.universalLocationRaw = val;
        this.locationResolveError = "";
        // Auto-resolve immediately when pasting a URL or direct coordinates
        if (
          val &&
          (/^https?:\/\//i.test(val) ||
            /maps\.app\.goo\.gl|goo\.gl\/maps|google\.[a-z.]+\/maps/i.test(val) ||
            /^[-+]?\d{1,2}(?:\.\d+)?\s*(?:,|;|\s+)\s*[-+]?\d{1,3}(?:\.\d+)?$/.test(val))
        ) {
          this.resolveUniversalLocationInput();
        }
      });
    },
    clearResolvedStationPreview() {
      this.resolvedStationPreview = null;
      this.currentPlace = null;
      this.locationResolveError = "";
      this.universalLocationRaw = "";
      if (this.$refs.gmapAutocomplete && this.$refs.gmapAutocomplete.$el) {
        this.$refs.gmapAutocomplete.$el.value = "";
      }
    },
    setPlace(place) {
      this.locationResolveError = "";
      if (!place || !place.geometry || !place.geometry.location) {
        // User pressed Enter on raw text/URL/coordinates in GmapAutocomplete without picking a suggestion
        this.resolveUniversalLocationInput();
        return;
      }
      this.currentPlace = place;
      const lat =
        typeof place.geometry.location.lat === "function"
          ? place.geometry.location.lat()
          : parseFloat(place.geometry.location.lat);
      const lng =
        typeof place.geometry.location.lng === "function"
          ? place.geometry.location.lng()
          : parseFloat(place.geometry.location.lng);

      if (isNaN(lat) || isNaN(lng)) {
        this.resolveUniversalLocationInput();
        return;
      }

      const defaultName = place.name || `محطة ${this.stops.length + 1}`;
      this.resolvedStationPreview = {
        name: defaultName,
        address: place.formatted_address || place.name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        lat,
        lng,
        place_id: place.place_id || `coord_${lat.toFixed(6)}_${lng.toFixed(6)}`,
        source_type: "google_places_autocomplete",
      };
      this.center = { lat, lng };
    },
    async resolveUniversalLocationInput() {
      const inputEl =
        this.$refs.gmapAutocomplete && this.$refs.gmapAutocomplete.$el
          ? this.$refs.gmapAutocomplete.$el
          : null;
      const raw = (inputEl && inputEl.value ? inputEl.value : this.universalLocationRaw || "").trim();
      this.universalLocationRaw = raw;

      if (!raw) {
        this.locationResolveError = "يرجى إدخال اسم مكان أو إحداثيات أو رابط Google Maps أولاً.";
        return null;
      }

      this.resolvingLocation = true;
      this.locationResolveError = "";

      try {
        const res = await axios.post("/google-routes/resolve-location", {
          input: raw,
        });
        if (res.data && res.data.status && res.data.data) {
          const d = res.data.data;
          const lat = parseFloat(d.lat);
          const lng = parseFloat(d.lng);
          this.resolvedStationPreview = {
            name: d.name || `محطة ${this.stops.length + 1}`,
            address: d.address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
            lat,
            lng,
            place_id: d.place_id || `coord_${lat.toFixed(6)}_${lng.toFixed(6)}`,
            source_type: d.source_type || "text_search",
          };
          this.center = { lat, lng };
          return this.resolvedStationPreview;
        } else {
          this.locationResolveError =
            (res.data && res.data.message) || "تعذر تحديد الموقع من المدخلات.";
          return null;
        }
      } catch (err) {
        this.locationResolveError =
          (err &&
            err.response &&
            err.response.data &&
            err.response.data.message) ||
          "تعذر تحليل الموقع المدخل. تأكد من صحة الرابط أو الإحداثيات.";
        return null;
      } finally {
        this.resolvingLocation = false;
      }
    },
    async handleAddLocationBtnClick() {
      if (!this.resolvedStationPreview) {
        const resolved = await this.resolveUniversalLocationInput();
        if (!resolved) return;
      }
      this.addResolvedStation();
    },
    addResolvedStation() {
      if (!this.resolvedStationPreview) return;
      const preview = this.resolvedStationPreview;
      const lat = parseFloat(preview.lat);
      const lng = parseFloat(preview.lng);

      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        this.locationResolveError = "إحداثيات المحطة غير صالحة.";
        return;
      }

      // 1. Check if duplicate in current Route
      const dupInRoute = this.findDuplicateInRoute(lat, lng, preview.place_id, null);
      if (dupInRoute) {
        this.showDuplicateStopWarning(dupInRoute.existingStop, preview.name);
        return;
      }

      // 2. Check if exists globally in saved stops (CASE A vs CASE C)
      const matchedGlobal = this.findMatchingGlobalStop(lat, lng, preview.place_id);
      let stop;
      if (matchedGlobal) {
        // CASE B: Location exists globally AND is already selected in current Route
        const dupGlobalInRoute = this.findDuplicateInRoute(matchedGlobal.lat, matchedGlobal.lng, matchedGlobal.place_id, matchedGlobal.id);
        if (dupGlobalInRoute) {
          this.showDuplicateStopWarning(dupGlobalInRoute.existingStop, preview.name || matchedGlobal.name);
          return;
        }
        // CASE A: Reuse existing valid global Stop record/id
        stop = {
          id: Number(matchedGlobal.id),
          name: matchedGlobal.name || (preview.name || "").trim() || `محطة ${this.stops.length + 1}`,
          address: matchedGlobal.address || preview.address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          lat: parseFloat(matchedGlobal.lat),
          lng: parseFloat(matchedGlobal.lng),
          place_id: matchedGlobal.place_id || preview.place_id || `coord_${lat.toFixed(6)}_${lng.toFixed(6)}`,
        };
      } else {
        // CASE C: New location -> create new stop, NEVER assign id = 0 or fake client id
        stop = {
          name: (preview.name || "").trim() || `محطة ${this.stops.length + 1}`,
          address: preview.address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          lat,
          lng,
          place_id: preview.place_id || `coord_${lat.toFixed(6)}_${lng.toFixed(6)}`,
        };
      }

      this.stops.push(stop);
      this.markers.push({
        place_id: stop.place_id,
        position: { lat: stop.lat, lng: stop.lng },
        infoText: `<strong>${stop.name}</strong><br/>${stop.address}`,
      });

      if (this.stops.length > 1) {
        const lastStop = this.stops[this.stops.length - 1];
        const beforeLastStop = this.stops[this.stops.length - 2];
        this.getAddedStopRoute(lastStop, beforeLastStop);
      }

      this.clearResolvedStationPreview();
    },
    handleMapClick(place) {
      this.currentPlace = place;
      this.addMarker(null);
    },
    addMarker(in_stop = null) {
      if (in_stop == null) {
        this.$swal
          .fire({
            title: this.$t("enter_stop_name") || "أدخل اسم المحطة",
            input: "text",
            inputValue: `محطة ${this.stops.length + 1}`,
            showCancelButton: true,
            cancelButtonText: this.$t("cancel") || "إلغاء",
            confirmButtonText: this.$t("save") || "حفظ",
            confirmButtonColor: "#082C69",
            customClass: {
              container: "swal-rtl",
            },
          })
          .then((result) => {
            if (result.isConfirmed) {
              const stop_name = (result.value || "").trim();
              this.addStop(null, stop_name);
            }
          });
      } else {
        this.addStop(in_stop);
      }
    },
    addStop(in_stop, stop_name = "") {
      const stop = in_stop != null ? in_stop : this.getStopFromPlace(this.currentPlace, stop_name);
      if (!stop) return;

      if (in_stop == null) {
        const dup = this.findDuplicateInRoute(stop.lat, stop.lng, stop.place_id, stop.id);
        if (dup) {
          this.showDuplicateStopWarning(dup.existingStop, stop.name);
          return;
        }
      }

      const position = {
        lat: parseFloat(stop.lat),
        lng: parseFloat(stop.lng),
      };

      this.markers.push({
        place_id: stop.place_id,
        position,
        infoText: `<strong>${stop.name}</strong><br/>${stop.address}`,
      });

      if (in_stop == null) {
        this.stops.push(stop);
        if (this.stops.length > 1) {
          const lastStop = this.stops[this.stops.length - 1];
          const beforeLastStop = this.stops[this.stops.length - 2];
          this.getAddedStopRoute(lastStop, beforeLastStop);
        }
      }

      this.currentPlace = null;
      if (this.$refs.gmapAutocomplete != null && this.$refs.gmapAutocomplete.$el) {
        this.$refs.gmapAutocomplete.$el.value = "";
      }
    },
    getStopFromPlace(place, stop_name) {
      if (!place) return null;
      const lat = typeof place.geometry.location.lat === "function" ? place.geometry.location.lat() : parseFloat(place.geometry.location.lat);
      const lng = typeof place.geometry.location.lng === "function" ? place.geometry.location.lng() : parseFloat(place.geometry.location.lng);
      const place_id = place.place_id || `coord_${lat.toFixed(6)}_${lng.toFixed(6)}`;

      const matchedGlobal = this.findMatchingGlobalStop(lat, lng, place_id);
      if (matchedGlobal) {
        return {
          id: Number(matchedGlobal.id),
          name: matchedGlobal.name || stop_name || place.name || `محطة ${this.stops.length + 1}`,
          address: matchedGlobal.address || place.formatted_address || place.name || "موقع محدد على الخريطة",
          lat: parseFloat(matchedGlobal.lat),
          lng: parseFloat(matchedGlobal.lng),
          place_id: matchedGlobal.place_id || place_id,
        };
      }

      return {
        name: stop_name || place.name || `محطة ${this.stops.length + 1}`,
        address: place.formatted_address || place.name || "موقع محدد على الخريطة",
        lat,
        lng,
        place_id,
      };
    },
    getStopId() {
      return null;
    },
    getAddedStopRoute(destinationStop, originStop, entry_index = null) {
      this.submitting = true;
      axios
        .get(
          `/google-routes/compute-route?origin_lat=${originStop.lat}&origin_lng=${originStop.lng}&destination_lat=${destinationStop.lat}&destination_lng=${destinationStop.lng}`
        )
        .then((response) => {
          if (response.data && response.data.routes) {
            const routes = response.data.routes.map((route, index) => ({
              summary: route.description || `المسار ${index + 1}`,
              overview_path: route.polyline.geoJsonLinestring.coordinates.map(([lng, lat]) => ({ lat, lng })),
              index,
              duration: route.duration || null,
            }));

            if (entry_index == null) {
              this.ordered_directions.push(routes);
              this.chosen_routes.push(0);
            } else {
              this.ordered_directions[entry_index] = routes;
              this.chosen_routes[entry_index] = 0;
            }
            this.setup_polyline();
          }
        })
        .catch((error) => {
          console.error("Could not compute route directions:", error);
          this.$notify({
            title: this.$t("Error") || "خطأ",
            text: this.$t("could_not_get_directions") || "تعذر حساب اتجاهات المسار بين المحطتين",
            type: "error",
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    deleteMarker(index) {
      this.$swal
        .fire({
          title: this.$t("are_you_sure") || "هل أنت متأكد؟",
          text: `هل تريد حذف المحطة "${this.stops[index]?.name}" من المسار؟`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$t("yes_delete") || "نعم، حذف",
          cancelButtonText: this.$t("cancel") || "إلغاء",
          confirmButtonColor: "#DC2626",
          cancelButtonColor: "#64748B",
          customClass: {
            container: "swal-rtl",
          },
        })
        .then((result) => {
          if (result.isConfirmed) {
            const stop = this.stops[index];
            for (let i = 0; i < this.markers.length; i++) {
              if (this.markers[i].place_id === stop.place_id) {
                this.markers.splice(i, 1);
                break;
              }
            }
            this.redoDirections(index);
            this.stops.splice(index, 1);
            this.selectedIdx = null;
            this.selectedItem = null;
            this.$notify({
              title: this.$t("Success") || "نجاح",
              text: "تم حذف المحطة بنجاح",
              type: "success",
            });
          }
        });
    },
    redoDirections(index) {
      if (this.stops.length <= 2) {
        this.ordered_directions = [];
        this.chosen_routes = [];
        this.setup_polyline();
        return;
      }
      if (index === 0) {
        this.ordered_directions.splice(0, 1);
        this.chosen_routes.splice(0, 1);
        this.setup_polyline();
      } else if (index === this.stops.length - 1) {
        this.ordered_directions.splice(index - 1, 1);
        this.chosen_routes.splice(index - 1, 1);
        this.setup_polyline();
      } else {
        this.ordered_directions.splice(index, 1);
        this.chosen_routes.splice(index, 1);
        const originStop = this.stops[index - 1];
        const destinationStop = this.stops[index + 1];
        this.getAddedStopRoute(destinationStop, originStop, index - 1);
      }
    },
    async moveStopUp(index) {
      if (index <= 0) return;
      const temp = this.stops[index];
      this.$set(this.stops, index, this.stops[index - 1]);
      this.$set(this.stops, index - 1, temp);
      this.selectedIdx = index - 1;
      await this.rebuildAllDirections();
    },
    async moveStopDown(index) {
      if (index >= this.stops.length - 1) return;
      const temp = this.stops[index];
      this.$set(this.stops, index, this.stops[index + 1]);
      this.$set(this.stops, index + 1, temp);
      this.selectedIdx = index + 1;
      await this.rebuildAllDirections();
    },
    async rebuildAllDirections() {
      if (this.stops.length < 2) {
        this.ordered_directions = [];
        this.chosen_routes = [];
        this.setup_polyline();
        return;
      }
      this.submitting = true;
      this.ordered_directions = [];
      this.chosen_routes = [];
      try {
        for (let i = 1; i < this.stops.length; i++) {
          const originStop = this.stops[i - 1];
          const destStop = this.stops[i];
          const res = await axios.get(
            `/google-routes/compute-route?origin_lat=${originStop.lat}&origin_lng=${originStop.lng}&destination_lat=${destStop.lat}&destination_lng=${destStop.lng}`
          );
          if (res.data && res.data.routes) {
            const routes = res.data.routes.map((route, idx) => ({
              summary: route.description || `المسار ${idx + 1}`,
              overview_path: route.polyline.geoJsonLinestring.coordinates.map(([lng, lat]) => ({ lat, lng })),
              index: idx,
              duration: route.duration || null,
            }));
            this.ordered_directions.push(routes);
            this.chosen_routes.push(0);
          }
        }
        this.setup_polyline();
      } catch (err) {
        console.error("Rebuild directions error:", err);
      } finally {
        this.submitting = false;
      }
    },
    setup_polyline() {
      this.polyline = [];
      for (let i = 0; i < this.ordered_directions.length; i++) {
        const chosen = this.chosen_routes[i] || 0;
        if (this.ordered_directions[i] && this.ordered_directions[i].length > chosen) {
          const path = {
            data: this.ordered_directions[i][chosen].overview_path,
            strokeColor: this.colors[i % this.colors.length],
          };
          this.polyline.push(path);
        }
      }
    },
    selectStop(element, index) {
      this.selectedIdx = index;
      this.selectedItem = element.place_id;
    },
    fitMapBounds() {
      if (this.$refs.mapLoader && typeof this.$refs.mapLoader.fitBounds === "function") {
        this.$refs.mapLoader.fitBounds();
      }
    },
    calcDistance(lat1, lon1, lat2, lon2) {
      const theta = lon1 - lon2;
      let dist =
        Math.sin((lat1 * Math.PI) / 180) * Math.sin((lat2 * Math.PI) / 180) +
        Math.cos((lat1 * Math.PI) / 180) *
          Math.cos((lat2 * Math.PI) / 180) *
          Math.cos((theta * Math.PI) / 180);
      dist = Math.acos(Math.min(Math.max(dist, -1), 1));
      dist = (dist * 180) / Math.PI;
      const miles = dist * 60 * 1.1515;
      return miles * 1.609344;
    },
    fetchStops() {
      this.submitting = true;
      axios
        .get(`/routes/${this.route_id}`)
        .then((response) => {
          const data = response.data;
          this.route_name = data.name;
          this.route_code = data.route_code || (data.id ? 'R-' + String(data.id).padStart(4, '0') : '');
          this.full_trip_price = data.full_trip_price !== undefined && data.full_trip_price !== null ? data.full_trip_price : "";
          this.minimum_percentage = data.minimum_percentage !== undefined && data.minimum_percentage !== null ? data.minimum_percentage : "";
          this.weekly_discount_percentage = data.weekly_discount_percentage !== undefined && data.weekly_discount_percentage !== null ? data.weekly_discount_percentage : "";
          this.monthly_discount_percentage = data.monthly_discount_percentage !== undefined && data.monthly_discount_percentage !== null ? data.monthly_discount_percentage : "";
          this.stops = Array.isArray(data.stops) ? data.stops : [];
          this.markers = [];
          this.stops.forEach((stop, index) => {
            stop.lat = parseFloat(stop.lat);
            stop.lng = parseFloat(stop.lng);
            this.markers.push({
              place_id: stop.place_id || String(stop.id),
              position: { lat: stop.lat, lng: stop.lng },
              infoText: `<strong>${stop.name}</strong><br/>${stop.address}`,
            });
          });

          // Restore saved directions and chosen route alternatives directly from database
          if (data.directions && Array.isArray(data.directions) && data.directions.length > 0) {
            this.ordered_directions = data.directions;
            this.chosen_routes = data.directions.map((legDirs) => {
              if (Array.isArray(legDirs)) {
                const currentIdx = legDirs.findIndex((d) => d.current == 1 || d.current === true);
                return currentIdx >= 0 ? legDirs[currentIdx].index : 0;
              }
              return 0;
            });
            this.setup_polyline();
          } else {
            this.rebuildAllDirections();
          }
        })
        .catch((error) => {
          console.error("Error fetching route stops:", error);
          this.$notify({
            title: this.$t("Error") || "خطأ",
            text: this.$t("error_fetching_stops_of_route") || "فشل تحميل محطات المسار",
            type: "error",
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    saveRoute() {
      if (!this.isStep1Valid) {
        this.goToStep(1);
        return;
      }
      if (this.stops.length < 2) {
        this.$swal.fire({
          title: "محطات غير كافية",
          text: "يجب إضافة محطتين على الأقل (نقطة بداية ونقطة نهاية) لإنشاء المسار.",
          icon: "warning",
          confirmButtonText: "حسناً",
          confirmButtonColor: "#082C69",
          customClass: {
            container: "swal-rtl",
          },
        });
        return;
      }

      const cleanedStops = this.stops.map((s) => {
        const item = { ...s };
        if (item.id === undefined || item.id === null || isNaN(Number(item.id)) || Number(item.id) <= 0) {
          delete item.id;
        } else {
          item.id = Number(item.id);
        }
        item.lat = parseFloat(item.lat);
        item.lng = parseFloat(item.lng);
        return item;
      });

      this.submitting = true;
      axios
        .post("/routes/create-edit", {
          id: this.route_id,
          route: this.route_name.trim(),
          stops: cleanedStops,
          chosen_routes: this.chosen_routes,
          ordered_directions: this.ordered_directions,
          full_trip_price: this.full_trip_price !== null && this.full_trip_price !== "" ? parseFloat(this.full_trip_price) : null,
          minimum_percentage: this.minimum_percentage !== null && this.minimum_percentage !== "" ? parseFloat(this.minimum_percentage) : null,
          weekly_discount_percentage: this.weekly_discount_percentage !== null && this.weekly_discount_percentage !== "" ? parseFloat(this.weekly_discount_percentage) : null,
          monthly_discount_percentage: this.monthly_discount_percentage !== null && this.monthly_discount_percentage !== "" ? parseFloat(this.monthly_discount_percentage) : null,
        })
        .then(() => {
          this.$notify({
            title: this.$t("Success") || "نجاح",
            text: this.mode === 1 ? "تم تحديث المسار بنجاح" : "تم إنشاء المسار بنجاح",
            type: "success",
          });
          this.$router.replace({ name: "routes" });
        })
        .catch((error) => {
          console.error("Save route error:", error);
          const errorMsg =
            error && error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "فشل حفظ بيانات المسار";
          this.$swal.fire("خطأ", errorMsg, "error");
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    confirmCancel() {
      if (this.stops.length > 0) {
        this.$swal
          .fire({
            title: "إلغاء إنشاء المسار؟",
            text: "هل أنت متأكد من رغبتك في الإلغاء؟ سيتم فقدان المحطات غير المحفوظة.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "نعم، إلغاء",
            cancelButtonText: "متابعة العمل",
            confirmButtonColor: "#DC2626",
            cancelButtonColor: "#64748B",
            customClass: {
              container: "swal-rtl",
            },
          })
          .then((result) => {
            if (result.isConfirmed) {
              this.$router.push({ name: "routes" });
            }
          });
      } else {
        this.$router.push({ name: "routes" });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* ==========================================================================
   Route Making — Create Route Page Styles
   Matching the approved Routes Management master design system
   Identity: BeepGo Navy (#082C69), Gray surface (#F8FAFD), Cairo font
   ========================================================================== */

.route-making-container {
  width: 100%;
  font-family: 'Cairo', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  color: #1E293B;
  background: transparent;
}

/* ==================== PAGE HEADER ==================== */
.rm-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;

  .rm-header-titles {
    .rm-badge-section {
      display: inline-flex;
      align-items: center;
      font-size: 12.5px;
      font-weight: 700;
      color: #082C69;
      background: rgba(8, 44, 105, 0.08);
      padding: 4px 12px;
      border-radius: 6px;
      margin-bottom: 6px;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(8, 44, 105, 0.15);
      }
    }

    .rm-main-title {
      font-size: 24px;
      font-weight: 800;
      color: #082C69;
      margin: 0 0 4px 0;
      line-height: 1.3;
    }

    .rm-subtitle {
      font-size: 14px;
      color: #64748B;
      margin: 0;
      font-weight: 500;
    }
  }

  .rm-header-actions {
    display: flex;
    align-items: center;
  }
}

/* ==================== BUTTON SYSTEM ==================== */
.rm-btn-primary {
  background: #082C69 !important;
  color: #FFFFFF !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  height: 44px !important;
  border-radius: 10px !important;
  padding: 0 20px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px rgba(8, 44, 105, 0.25) !important;
  transition: all 0.2s ease !important;

  &:hover {
    background: #061B40 !important;
    box-shadow: 0 6px 18px rgba(8, 44, 105, 0.35) !important;
    transform: translateY(-1px);
  }
}

.rm-btn-secondary {
  background: #FFFFFF !important;
  color: #475569 !important;
  border: 1px solid #CBD5E1 !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  height: 44px !important;
  border-radius: 10px !important;
  padding: 0 18px !important;
  text-transform: none !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03) !important;
  transition: all 0.2s ease !important;

  &:hover {
    background: #F8FAFC !important;
    border-color: #94A3B8 !important;
    color: #1E293B !important;
  }
}

.rm-btn-text {
  font-weight: 600 !important;
  font-size: 13.5px !important;
  text-transform: none !important;
}

/* ==================== CARDS BASE ==================== */
.rm-card {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 22px 24px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 2px 10px rgba(8, 44, 105, 0.03);
  transition: all 0.2s ease;

  .rm-card-header {
    .rm-card-title {
      font-size: 16px;
      font-weight: 800;
      color: #082C69;
      margin: 0 0 2px 0;
    }

    .rm-card-subtitle {
      font-size: 12.5px;
      color: #64748B;
      margin: 0;
      font-weight: 500;
    }
  }
}

.rm-icon-header-box {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(8, 44, 105, 0.07);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ==================== 2-STEP STEPPER ==================== */
.rm-stepper-card {
  background: #FFFFFF;
  border-radius: 14px;
  padding: 16px 28px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 2px 8px rgba(8, 44, 105, 0.02);

  .rm-stepper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;

    .rm-step-item {
      display: flex;
      align-items: center;
      gap: 14px;
      cursor: pointer;
      user-select: none;
      transition: all 0.2s ease;

      .rm-step-circle {
        width: 38px;
        height: 38px;
        border-radius: 50%;
        background: #F1F5F9;
        color: #64748B;
        font-weight: 800;
        font-size: 15px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.25s ease;
        border: 2px solid #E2E8F0;
      }

      .rm-step-info {
        display: flex;
        flex-direction: column;

        .rm-step-seq {
          font-size: 11px;
          color: #94A3B8;
          font-weight: 600;
        }

        .rm-step-title {
          font-size: 14.5px;
          font-weight: 700;
          color: #64748B;
        }
      }

      &.step-active {
        .rm-step-circle {
          background: #082C69;
          border-color: #082C69;
          color: #FFFFFF;
          box-shadow: 0 4px 12px rgba(8, 44, 105, 0.25);
        }

        .rm-step-info {
          .rm-step-seq {
            color: #F59E0B;
          }
          .rm-step-title {
            color: #082C69;
          }
        }
      }

      &.step-completed {
        .rm-step-circle {
          background: #10B981;
          border-color: #10B981;
          color: #FFFFFF;
        }

        .rm-step-info {
          .rm-step-title {
            color: #1E293B;
          }
        }
      }

      &.step-disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    .rm-step-divider {
      flex-grow: 1;
      height: 2px;
      background: #E2E8F0;
      transition: all 0.3s ease;

      &.divider-completed {
        background: #10B981;
      }
    }
  }
}

/* ==================== STEP 1 CARD ==================== */
.rm-step1-container {
  max-width: 680px;
  margin: 0 auto;

  .rm-step1-card {
    padding: 32px 36px;
  }
}

/* ==================== FORM ELEMENTS ==================== */
.rm-form-group {
  display: flex;
  flex-direction: column;

  .rm-form-label {
    font-size: 13.5px;
    font-weight: 700;
    color: #1E293B;
    margin-bottom: 6px;

    .rm-required-star {
      color: #EF4444;
      margin-inline-start: 4px;
    }
  }

  .rm-field-hint {
    font-size: 12px;
    color: #64748B;
    line-height: 1.4;
  }
}

::v-deep .v-text-field--outlined.rm-custom-input .v-input__slot {
  background: #F8FAFC !important;
  border-radius: 10px !important;
  min-height: 44px !important;
}

::v-deep .v-select--outlined.rm-custom-select .v-input__slot,
::v-deep .v-autocomplete--outlined.rm-custom-select .v-input__slot {
  background: #F8FAFC !important;
  border-radius: 10px !important;
  min-height: 42px !important;
}

/* ==================== STEP 2 PAIRED ROW GRID ==================== */
.rm-step2-container {
  width: 100%;
}

.rm-step2-grid {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.rm-row-paired {
  display: grid;
  grid-template-columns: minmax(0, 42%) minmax(0, 58%);
  gap: 24px;
  align-items: stretch;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

.rm-col-controls {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.rm-col-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.rm-full-height {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* ==================== MAP VIEWPORT ==================== */
.rm-map-viewport {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #E2E8F0;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
  min-height: 420px;
  height: 100%;
}

/* ==================== COMPACT PRICING GRID ==================== */
.rm-pricing-inputs-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.rm-derived-summary-box {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 10px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 10px 14px;
}

.rm-derived-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rm-derived-label {
  font-size: 11.5px;
  color: #64748B;
  font-weight: 600;
}

.rm-derived-val {
  font-size: 13.5px;
  color: #082C69;
  font-weight: 700;

  &.rm-val-highlight {
    color: #10B981;
  }
}

/* ==================== SUBSCRIPTION PACKAGES ==================== */
.rm-packages-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
}

.rm-pkg-card {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.rm-pkg-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.rm-tag-days {
  font-size: 11px;
  font-weight: 700;
  color: #082C69;
  background: rgba(8, 44, 105, 0.08);
  padding: 2px 8px;
  border-radius: 6px;
}

.rm-pkg-preview {
  background: #FFFFFF;
  border: 1px dashed #CBD5E1;
  border-radius: 8px;
  padding: 10px;
  margin-top: auto;
}

.rm-pkg-preview-title {
  font-size: 11px;
  font-weight: 700;
  color: #64748B;
  margin-bottom: 6px;
}

.rm-pkg-preview-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11.5px;
  color: #475569;
  margin-bottom: 4px;

  &.text-danger {
    color: #EF4444;
  }
}

.rm-pkg-preview-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 800;
  color: #082C69;
  padding-top: 6px;
  margin-top: 4px;
  border-top: 1px solid #F1F5F9;
}

.rm-pkg-total-val {
  color: #10B981;
}

/* ==================== BOTTOM ACTIONS ==================== */
.rm-bottom-actions-card {
  padding: 16px 20px;
  border-radius: 12px;
}

.rm-stops-counter-chip {
  background: rgba(8, 44, 105, 0.08) !important;
  color: #082C69 !important;
}

.rm-fit-btn {
  color: #082C69 !important;
  font-weight: 700 !important;
  border-radius: 8px !important;
}

/* ==================== STOPS TABLE ==================== */
.rm-table-count {
  font-size: 12.5px;
  font-weight: 700;
  color: #64748B;
}

.rm-stops-empty {
  border: 2px dashed #E2E8F0;
  border-radius: 12px;
  background: #F8FAFC;

  .rm-empty-sub {
    font-size: 15px;
    font-weight: 700;
    color: #475569;
  }

  .rm-empty-text {
    font-size: 13px;
  }
}

.rm-stops-table-wrap {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #E2E8F0;
}

.rm-modern-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  text-align: right;

  thead tr {
    background: #F8FAFC;
    border-bottom: 1px solid #E2E8F0;

    th {
      padding: 12px 14px;
      font-size: 12px;
      font-weight: 700;
      color: #475569;
      white-space: nowrap;
      border-bottom: 1px solid #E2E8F0;
    }
  }

  tbody tr {
    transition: background 0.15s ease;
    border-bottom: 1px solid #F1F5F9;
    cursor: pointer;

    &:hover {
      background: #F8FAFD;
    }

    &.row-active {
      background: rgba(8, 44, 105, 0.04);
    }

    &:last-child td {
      border-bottom: none;
    }

    td {
      padding: 12px 14px;
      vertical-align: middle;
      border-bottom: 1px solid #F1F5F9;
    }
  }
}

.rm-order-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #F1F5F9;
  color: #475569;
  font-size: 12px;
  font-weight: 700;

  &.badge-first {
    background: rgba(16, 185, 129, 0.15);
    color: #10B981;
  }

  &.badge-last {
    background: rgba(239, 68, 68, 0.15);
    color: #EF4444;
  }
}

.rm-route-icon-circle {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rm-stop-name-text {
  font-weight: 700;
  color: #1E293B;
}

.rm-tag-first {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 700;
  color: #10B981;
  background: rgba(16, 185, 129, 0.1);
  padding: 1px 6px;
  border-radius: 4px;
}

.rm-tag-last {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 700;
  color: #EF4444;
  background: rgba(239, 68, 68, 0.1);
  padding: 1px 6px;
  border-radius: 4px;
}

.rm-address-text {
  color: #64748B;
  font-size: 12px;
  max-width: 220px;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rm-dir-start-tag {
  color: #94A3B8;
  font-size: 11.5px;
  font-style: italic;
}

.rm-direction-select {
  max-width: 220px;
  font-size: 12px;
}

.rm-actions-group {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  .rm-action-btn {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    color: #64748B;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
      background: #F1F5F9;
      color: #082C69;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &.btn-delete:hover:not(:disabled) {
      background: #FEE2E2;
      color: #DC2626;
    }
  }
}

/* ==================== SUMMARY METRICS CARD ==================== */
.rm-summary-metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }

  .rm-metric-box {
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;

    .rm-metric-label {
      font-size: 12px;
      font-weight: 700;
      color: #64748B;
      margin-bottom: 4px;
    }

    .rm-metric-val-wrap {
      min-height: 32px;
      display: flex;
      align-items: center;

      .rm-metric-val {
        font-size: 22px;
        font-weight: 800;
        color: #082C69;

        small {
          font-size: 13px;
          font-weight: 600;
          color: #64748B;
        }
      }

      .rm-metric-empty {
        min-height: 24px;
        min-width: 24px;
      }
    }

    .rm-metric-caption {
      font-size: 11px;
      color: #94A3B8;
      margin-top: 2px;
    }
  }
}

/* ==================== CONTROL SIDEBAR ==================== */
.rm-or-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: #94A3B8;
  font-size: 12px;
  font-weight: 700;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #E2E8F0;
  }

  span {
    padding: 0 10px;
  }
}

.rm-gmap-input-wrapper {
  .rm-gmap-input {
    width: 100%;
    height: 42px;
    background: #F8FAFC;
    border: 1px solid #CBD5E1;
    border-radius: 10px;
    padding: 0 14px;
    font-size: 13px;
    font-family: inherit;
    color: #1E293B;
    outline: none;
    transition: all 0.2s ease;

    &:focus {
      border-color: #082C69;
      background: #FFFFFF;
      box-shadow: 0 0 0 3px rgba(8, 44, 105, 0.08);
    }
  }
}

.rm-info-banner {
  background: rgba(8, 44, 105, 0.04);
  border: 1px solid rgba(8, 44, 105, 0.12);
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  align-items: flex-start;

  .rm-info-text {
    font-size: 12px;
    color: #334155;
    line-height: 1.5;
  }
}

.rm-code-display-box {
  display: inline-flex;
  align-items: center;
  background: #F1F5F9;
  border: 1px solid #CBD5E1;
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 15px;
  font-weight: 700;
  color: #082C69;
  letter-spacing: 0.5px;
}

.rm-readonly-badge {
  font-size: 11px;
  font-weight: 600;
  color: #64748B;
  background: #F1F5F9;
  padding: 2px 8px;
  border-radius: 6px;
}

.border-top {
  border-top: 1px solid #F1F5F9;
}
</style>
